import { Component, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { SubscribedComponent } from '../subscribed.component';
import { MarketVacancy, MarketVacancyInterest, InterestLevel, MarketVacancyTabCounts } from '../interfaces/market-vacancy';
import { MarketVacancyService } from '../shared/market-vacancy.service';
import { Globals } from '../shared/Globals';
import { AuthService } from '../shared/auth.service';
import { ActivatedRoute, Router } from '@angular/router';

import { Paged } from '../interfaces/paged';
import {
  activeFilterKeys,
  DualAxisComponent,
  DualAxisDelegate,
  DualAxisEvent,
  DualAxisPageLoadedEvent
} from '../dual-axis/dual-axis.component';
import { ColDef, ColType } from '../interfaces/colDef';
import { TreeNode } from '../interfaces/treenode';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { TableOptions } from '../settings/table-options/table-options.component';
import { SettingsService } from '../shared/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { NbDialogService } from '@nebular/theme';
import { PopupDialogComponent } from '../dialogs/popup-dialog/popup-dialog.component';
import { MarketVacancyDetailComponent } from './market-vacancy-detail.component';
import { CreateVacancyDialogComponent } from './create-vacancy-dialog/create-vacancy-dialog.component';
import { MarketFilterSheetComponent } from './market-filter-sheet/market-filter-sheet.component';

type VacancyMutationPayload = { vacancyId: number; hidden?: boolean; interested?: boolean };

/** Sentinel id for the locally-pushed "my own heart" row on the Team tab, before
 *  a refetch supplies the real one. Only ever one such row per user, so it does
 *  not collide with the `track interest.id` in the avatar cluster. */
const OPTIMISTIC_INTEREST_ID = -1;


@Component({
    selector: 'bst-market-vacancies-list',
    templateUrl: './market-vacancies-list.component.html',
    styleUrls: ['./market-vacancies-list.component.scss'],
    standalone: false
})
export class MarketVacanciesListComponent extends SubscribedComponent implements DualAxisDelegate<MarketVacancy> {

  @ViewChild('cellTemplate', { static: true }) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', { static: true }) cardTemplateOutlet: TemplateRef<any>;
  // Shared team-interest avatar cluster, rendered into both the table cell and
  // the card via ngTemplateOutlet. Bridged through a @ViewChild because a bare
  // #teamCluster ref does not resolve across the cell/card <ng-template> views.
  @ViewChild('teamCluster', { static: true }) teamClusterOutlet: TemplateRef<any>;
  public forceCards = true; // vacancy cards at all widths (spec: phase-b-design §1); user-toggleable

  public openOptions(): void { this.events.next(DualAxisEvent.OPENFILTERS); }
  public createVacancy(): void {
    const dialogRef = this.dialogService.open(CreateVacancyDialogComponent, {
      dialogClass: 'wide-vacancy-dialog',
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });
    this.subs.push(dialogRef.onClose.subscribe((created: MarketVacancy | null) => {
      if (created) {
        // Refresh, not reset: the default sort is is_new/match_score rather than
        // posting_date, so a newly created vacancy is not necessarily on page 1,
        // and jumping there would cost the reader their place for nothing.
        this.events.next(DualAxisEvent.REFRESH);
        this.loadTabCounts();
      }
    }));
  }

  public toggleViewMode(): void {
    this.forceCards = !this.forceCards;
    this.settings.putUserSetting('table_market_vacancies_viewmode', this.forceCards ? 'cards' : 'table');
    this.settings.saveUserSettings(true);
  }

  public isDeadlineSoon(v: MarketVacancy): boolean {
    if (!v?.deadline) { return false; }
    const days = (new Date(v.deadline).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 7;
  }
  @ViewChild(DualAxisComponent) dualAxis: DualAxisComponent<MarketVacancy>;

  public data: TreeNode<MarketVacancy>[] = [];
  public stickyColumnsLeft: ColDef[] = [];
  public standardColumns: ColDef[] = [];
  public stickyColumnsRight: ColDef[] = [];
  public otherColumns: ColDef[] = [];
  public options: TableOptions;
  public events = new Subject<DualAxisEvent>();
  public sorts: {col: string, dir: number}[] = [];
  public tabs: any[] = [];
  public unseenCount: number = 0;
  public tabCounts: MarketVacancyTabCounts | null = null;
  private tabCountsRequestId = 0;

  public InterestLevel = InterestLevel;
  public DualAxisEvent = DualAxisEvent;
  public Globals = Globals;

  // Column references
  private sourceColumn: ColDef;
  private companyColumn: ColDef;
  private locationColumn: ColDef;

  // Search functionality
  public searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private pagedGeneration = 0;

  // Debounced tab-counts refresh, triggered after vacancy mutations (interest/hide
  // toggles) so rapidly toggling several rows does not fire one full tab-counts
  // request per toggle. The tab-switch/initial-load call stays undebounced.
  private mutationCountsRefresh$ = new Subject<void>();

  // User picker for per-user match score filtering
  public curUser = new FormControl(null);

  constructor(
    public auth: AuthService,
    private marketVacancyService: MarketVacancyService,
    private router: Router,
    private location: Location,
    private dialogService: NbDialogService,
    private settings: SettingsService,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();

    // Define tabs for status filtering
    this.tabs = [
      {
        label: this.translate.instant('vacancy.tab.active'),
        title: this.translate.instant('vacancy.tab.active'),
        route: '/market/overview/active',
        responsive: true
      },
      {
        label: 'My interests',
        title: 'My interests',
        route: '/market/overview/interests',
        responsive: true
      },
      {
        label: this.translate.instant('vacancy.tab.team'),
        title: this.translate.instant('vacancy.tab.team'),
        route: '/market/overview/team',
        responsive: true
      },
      {
        label: 'Hidden',
        title: 'Hidden',
        route: '/market/overview/hidden',
        responsive: true
      },
      {
        label: 'Inactive',
        title: 'Inactive',
        route: '/market/overview/inactive',
        responsive: true
      }
    ];

    // Team interests is scoped/403'd for plain users by MarketVacancyPolicy; keep
    // the tab out of the list for them rather than surfacing a dead link.
    if (this.auth.isUser) {
      this.tabs = this.tabs.filter(tab => tab.route !== '/market/overview/team');
    }

    // translate.instant(...) above can return the raw key if the lang file has
    // not yet loaded. Re-resolve via get(), which emits once translations are
    // available. ('My interests'/'Hidden'/'Inactive' are hard-coded English so
    // they are unaffected.)
    this.translate.get(['vacancy.tab.active', 'vacancy.tab.team']).subscribe(t => {
      const active = this.tabs.find(x => x.route === '/market/overview/active');
      if (active) { active.label = t['vacancy.tab.active']; }
      const team = this.tabs.find(x => x.route === '/market/overview/team');
      if (team) { team.label = t['vacancy.tab.team']; }
      this.refreshTabLabels();
    });

    // Define table columns
    this.stickyColumnsLeft = [
      { name: 'title', title: 'Title', type: ColType.STRING, required: false, sort: true },
    ];

    // Company column - text search filter
    this.companyColumn = {
      name: 'company',
      title: 'Company',
      type: ColType.STRING,
      required: false,
      sort: true
    };

    // Source column - text search filter
    this.sourceColumn = {
      name: 'source',
      title: 'Source',
      type: ColType.STRING,
      required: false,
      sort: true
    };

    // Location column - text search filter
    this.locationColumn = {
      name: 'location',
      title: 'Location',
      type: ColType.STRING,
      required: false,
      sort: true
    };

    this.standardColumns = [
      this.companyColumn,
      this.sourceColumn,
      this.locationColumn,
      { name: 'deadline', title: 'Deadline', type: ColType.DATE, required: false, sort: true },
      { name: 'start_date', title: 'Start date', type: ColType.DATE, required: false, sort: true },
      { name: 'end_date', title: 'enddate', type: ColType.DATE, required: false, sort: true },
      { name: 'posting_date', title: 'postingdate', type: ColType.DATE, required: false, sort: true },
      { name: 'skill', title: 'hardskills', type: ColType.LISTADDER, required: false },
      { name: 'development', title: 'softskills', type: ColType.LISTADDER, required: false },
      { name: 'certification', title: 'certifications', type: ColType.LISTADDER, required: false },
      // Same shape as domain_id below: renders the enriched industries relation, filters via MarketVacancyQueryBuilder::industry_id().
      { name: 'industry_id', title: 'industry', type: ColType.LISTADDER, required: false, filter: 'industry', sort: false },
      // filter: 'domain' points the list-adder typeahead at /domains/find (SearchController::domain_search());
      // the column name itself is the filter key MarketVacancyQueryBuilder::domain_id() dispatches on.
      { name: 'domain_id', title: 'userdomains', type: ColType.LISTADDER, required: false, filter: 'domain' },
      { name: 'hours_per_week', title: 'vacancy.hoursperweek', type: ColType.STRING, required: false },
      { name: 'contract_duration', title: 'vacancy.contractduration', type: ColType.STRING, required: false },
      { name: 'tariff', title: 'vacancy.tariff', type: ColType.STRING, required: false },
      { name: 'years_experience', title: 'experience', type: ColType.STRING, required: false },
    ];

    this.loadUnseenCount();

    // Users who never chose a sort have nothing persisted and pick up the composite
    // default below automatically; users who did choose one keep it (DualAxis.reset()
    // re-applies the saved value). New-first is the new default, not a forced migration.
    this.stickyColumnsRight = [
      // 140 (not 130): the header's in-flow sort + filter buttons (72px) plus
      // "Nieuw" would exactly fill 130 with no margin for font differences.
      { name: 'is_new', title: 'vacancy.col.new', type: ColType.BOOL, required: false, sort: true, stickyWidth: 140 },
      // range 0-100 so the filter dialog's INT dual-slider gets a valid upper bound
      // (without it the slider falls back to {gte:0, lte: undefined} -> NaN step / dead thumb).
      // "Match score" (not "Score") to distinguish this from a plain field name; /100 (no
      // "%") keeps it from reading as a probability.
      { name: 'match_score', title: 'Match score', type: ColType.INT, required: false, sort: true, stickyWidth: 190, range: { gte: 0, lte: 100 } },
    ];

    // Get status from route parameter
    this.subs.push(this.route.params.subscribe(params => {
      let status = 1; // Default to Active
      const statusParam = params['status'];
      if (statusParam === 'interests') {
        status = 2; // My Interests
      } else if (statusParam === 'hidden') {
        status = 3; // Hidden
      } else if (statusParam === 'inactive') {
        status = 0; // Inactive
      } else if (statusParam === 'team') {
        status = 4; // Team interests
      } else if (statusParam === 'active') {
        status = 1; // Active
      }

    const vm = this.settings.userSetting('table_market_vacancies_viewmode');
      if (vm) { this.forceCards = vm === 'cards'; }

      this.options = {
        display: this.settings.mergeTableColumns('table_market_vacancies', this.standardColumns),
        sorts: [{ col: 'is_new', dir: 2 }, { col: 'match_score', dir: 2 }], // Default: new first, then best match
        filter: {},
        others: {
          status: status
        }
      };

      // Restore the persisted per-user match filter. user_id lives in the shared
      // 'table_market_vacancies_filters' key (the same store DualAxis uses for
      // column filters) and is the authority; the saved user object is only the
      // picker's display value. Seed the filter here ourselves so the selection
      // survives the vacancy-detail round-trip regardless of when DualAxis
      // subscribes, then re-show the picker (the valueChanges guard below absorbs
      // the list-adder CVA re-fire, so this does not trigger a second load).
      let savedFilters = this.settings.userSetting('table_market_vacancies_filters');
      // The Industry column moved from a text filter on 'industry' to 'industry_id' (#1652). DualAxis
      // restores every saved key, so a stale 'industry' filter or sort would apply with no visible column to clear it.
      if (savedFilters && 'industry' in savedFilters) {
        savedFilters = { ...savedFilters };
        delete savedFilters['industry'];
        this.settings.putUserSetting('table_market_vacancies_filters', savedFilters);
      }
      const savedSorts = this.settings.userSetting('table_market_vacancies_sorts');
      if (Array.isArray(savedSorts) && savedSorts.some(s => s?.col === 'industry')) {
        this.settings.putUserSetting('table_market_vacancies_sorts', savedSorts.filter(s => s?.col !== 'industry'));
      }
      const savedUserId = savedFilters ? savedFilters['user_id'] : null;
      const savedUser = this.settings.userSetting('table_market_vacancies_user');
      if (savedUserId && savedUser && savedUser.id === savedUserId) {
        this.options.filter['user_id'] = savedUserId;
        this.curUser.setValue(savedUser, { emitEvent: false });
      } else if (savedUserId) {
        // Stale user_id with no matching display user (e.g. persisted by an older
        // build via the column-filter save path). Drop it so DualAxis.reset()
        // cannot silently scope the list to a user the picker cannot show.
        const cleaned = { ...savedFilters };
        delete cleaned['user_id'];
        this.settings.putUserSetting('table_market_vacancies_filters', cleaned);
        this.settings.removeUserSetting('table_market_vacancies_user');
      }

      // The candidate picker applies on the catalog tabs (Active, Inactive);
      // disable it on the personal tabs (My Interests / Hidden), which show your
      // own items. The selection is kept and reapplied when you return.
      if (this.candidateScopeApplies) {
        this.curUser.enable({ emitEvent: false });
      } else {
        this.curUser.disable({ emitEvent: false });
      }

      // Trigger data load
      this.events.next(DualAxisEvent.RESET);
      this.loadTabCounts();
    }));

    // Debounced text search
    this.subs.push(
      this.searchSubject.pipe(debounceTime(500)).subscribe(query => {
        this.applySearch(query);
      })
    );

    // Debounced tab-counts refresh after vacancy mutations (see mutationCountsRefresh$).
    this.subs.push(
      this.mutationCountsRefresh$.pipe(debounceTime(300)).subscribe(() => this.loadTabCounts())
    );

    // User picker: filter vacancies by selected user's match scores, and
    // persist the selection the same way column filters are persisted.
    this.subs.push(
      this.curUser.valueChanges.subscribe(user => {
        if (!this.options?.filter) return;
        const newId = (user && user.id) ? user.id : null;
        const curId = this.options.filter['user_id'] ?? null;
        // Absorb the list-adder CVA re-fire (its writeValue() calls onChange, so
        // even a programmatic setValue with emitEvent:false re-emits here).
        if (newId === curId) return;
        if (newId) {
          this.options.filter['user_id'] = newId;
        } else {
          delete this.options.filter['user_id'];
        }
        this.persistUserFilter(user || null);
        this.events.next(DualAxisEvent.RESET);
      })
    );
  }

  /** Persist the per-user match filter like DualAxis.filter() persists column
   *  filters (shared 'table_market_vacancies_filters' key, minus stuff/status;
   *  keep that strip list in sync with DualAxis.filter()), and keep the picked
   *  user object for re-displaying the picker on return. */
  private persistUserFilter(user: any): void {
    const filtersToSave = { ...this.options.filter };
    delete filtersToSave['stuff'];
    delete filtersToSave['status'];
    this.settings.putUserSetting('table_market_vacancies_filters', filtersToSave);
    if (user) {
      this.settings.putUserSetting('table_market_vacancies_user', user);
    } else {
      this.settings.removeUserSetting('table_market_vacancies_user');
    }
  }

  public optionsChange(value: TableOptions): boolean {
    return value.others.status !== this.options.others.status;
  }

  /** Rows are rebuilt from a fresh response on every fetch, so identity has to
   *  come from the vacancy rather than the wrapper object; without this the
   *  whole list's DOM (and any focus inside it) is recreated on each refresh.
   *
   *  Ids are safe as keys: the sort's match_score join can fan out (the match
   *  tables are unique on a triple that includes Client_id, which the join only
   *  constrains when a client scope is set), but QueryRequestBuilder::execute()
   *  re-reads the page through whereIn('id', ...), so each vacancy reaches the
   *  frontend once. The fan-out's live symptom is an inflated total/last_page
   *  against fewer rendered rows -- a backend counting bug, filed separately. */
  public trackRow = (node: TreeNode<MarketVacancy>): any => node.data?.id ?? node;

  public async paged(page: number, pageSize: number): Promise<Paged<MarketVacancy>> {
    const generation = ++this.pagedGeneration;
    // Copy so we never mutate the in-memory options.filter (the valueChanges
    // de-dupe guard reads options.filter['user_id']). The candidate scope is
    // only sent on the catalog tabs; appliedCandidateId encodes that rule.
    const filters: any = { ...(this.options.filter || {}) };
    filters.status = this.options.others.status ?? 1;
    if (this.appliedCandidateId === null) {
      delete filters['user_id'];
    }

    const result = await this.marketVacancyService.findMarketVacancies(page, filters, this.options.sorts || [], pageSize);

    // A newer paged() call started (and will resolve independently) since this
    // one began — don't let a stale, later-arriving response overwrite rows a
    // newer request already owns. DualAxis's pageRequestToken guards pageResult,
    // loading and the URL for a superseded request, but this.data is mutated
    // here, inside the delegate, one layer outside that guard — this generation
    // counter is what protects the rows.
    if (generation === this.pagedGeneration) {
      // Classic paged mode: each page fully replaces the prior page's rows.
      if (result && result.data) {
        result.data.forEach(vacancy => {
          if (typeof vacancy.is_new !== 'boolean') {
            vacancy.is_new = !vacancy.seen || vacancy.seen.length === 0;
          }
        });
        this.data = result.data.map(vacancy => ({
          id: vacancy.id,
          data: vacancy,
          children: []
        }));
      } else {
        this.data = [];
      }
    }

    return result;
  }

  public vacancyUrl(vacancy: MarketVacancy): string {
    const forUser = this.appliedCandidateId;
    const url = `/market/vacancies/${vacancy.id}`;
    return typeof forUser === 'number' ? `${url}?for_user=${forUser}` : url;
  }

  public onOpenVacancy(vacancy: MarketVacancy, event: MouseEvent) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    this.markSeen(vacancy);
    this.previewVacancy(vacancy);
  }

  private previewVacancy(vacancy: MarketVacancy) {
    const componentContext: any = { id: vacancy.id, embedded: true };
    const forUser = this.appliedCandidateId;
    if (typeof forUser === 'number') {
      componentContext.forUserId = forUser;
    }
    const shareUrl = `/market/vacancies/${vacancy.id}`;
    const backUrl = this.location.path();
    this.location.go(shareUrl);
    const dialogRef = this.dialogService.open<PopupDialogComponent<MarketVacancyDetailComponent>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      context: {
        comp: MarketVacancyDetailComponent,
        componentContext
      }
    });
    const locSub = this.location.subscribe(() => dialogRef.close());
    this.subs.push(dialogRef.onClose.subscribe(result => {
      locSub.unsubscribe();
      if (this.location.path() === shareUrl) this.location.go(backUrl);
      this.applyVacancyMutation(result);
    }));
  }

  private markSeen(vacancy: MarketVacancy): void {
    if (vacancy.is_new) {
      const previousCount = this.unseenCount;
      vacancy.is_new = false;
      if (this.unseenCount > 0) {
        this.unseenCount--;
      }
      this.marketVacancyService.markSeen([vacancy.id]).catch(() => {
        vacancy.is_new = true;
        this.unseenCount = previousCount;
      });
    }
  }

  public hasInterest(vacancy: MarketVacancy): boolean {
    return vacancy.interests?.length > 0;
  }

  public hasHidden(vacancy: MarketVacancy): boolean {
    return vacancy.hidden?.length > 0;
  }

  /** Vacancy ids with an interest/hide call still in flight, so a second click
   *  cannot send a conflicting mutation. The id is released once the server has
   *  answered; the list refetch that a row removal triggers is not awaited, so
   *  the button re-arms while that refetch is still running. */
  public readonly pendingMutations = new Set<number>();

  public isMutating(vacancy: MarketVacancy): boolean {
    return this.pendingMutations.has(vacancy.id);
  }

  /** A refused mutation leaves the row disagreeing with the server. The most
   *  common cause is a 404 -- the record is already gone, typically because
   *  another tab removed it. Simply keeping the row would strand it:
   *  the heart stays filled, every retry 404s again, and only a full reload
   *  clears it. Re-read the page instead, so the list converges on server truth
   *  without claiming the mutation succeeded. */
  private reconcileAfterRefusedMutation(): void {
    this.dualAxis?.refresh();
    this.mutationCountsRefresh$.next();
  }

  public async toggleInterest(vacancy: MarketVacancy, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.pendingMutations.has(vacancy.id)) { return; }
    this.pendingMutations.add(vacancy.id);
    try {
      const interested = !this.hasInterest(vacancy);
      if (interested) {
        const forUser = this.appliedCandidateId;
        await this.marketVacancyService.expressInterest(vacancy.id, typeof forUser === 'number' ? forUser : undefined);
      } else {
        await this.marketVacancyService.withdrawInterest(vacancy.id);
      }
      this.applyVacancyMutation(vacancy.id, { interested });
    } catch (error) {
      // The server refused and our row now disagrees with it (a 409 means the
      // state we are trying to set is already set). Reconcile rather than leave
      // the row asserting something the server denies.
      console.error('Failed to toggle interest', error);
      this.reconcileAfterRefusedMutation();
    } finally {
      this.pendingMutations.delete(vacancy.id);
    }
  }

  public async toggleHide(vacancy: MarketVacancy, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.pendingMutations.has(vacancy.id)) { return; }
    this.pendingMutations.add(vacancy.id);
    try {
      const hidden = !this.hasHidden(vacancy);
      if (hidden) {
        await this.marketVacancyService.hideVacancy(vacancy.id);
      } else {
        await this.marketVacancyService.unhideVacancy(vacancy.id);
      }
      this.applyVacancyMutation(vacancy.id, { hidden });
    } catch (error) {
      // The server refused and our row now disagrees with it (a 409 means the
      // state we are trying to set is already set). Reconcile rather than leave
      // the row asserting something the server denies.
      console.error('Failed to toggle hide', error);
      this.reconcileAfterRefusedMutation();
    } finally {
      this.pendingMutations.delete(vacancy.id);
    }
  }

  private applyVacancyMutation(vacancyIdOrPayload: number | VacancyMutationPayload | any, mutation: Partial<VacancyMutationPayload> = {}): void {
    const payload = typeof vacancyIdOrPayload === 'number'
      ? { vacancyId: vacancyIdOrPayload, ...mutation }
      : (vacancyIdOrPayload?.closePayload ? vacancyIdOrPayload.closePayload() : vacancyIdOrPayload);
    if (!payload?.vacancyId) return;

    const status = this.options?.others?.status;

    // Apply the in-place relation mutations first (including the team_interests
    // splice on the Team tab) so the row-removal decision below reads the
    // post-toggle cluster length; without this it would read the stale
    // pre-splice array and keep a row that should disappear.
    this.data
      .filter(node => node.data.id === payload.vacancyId)
      .forEach(node => {
        const vacancy = node.data;
        if (payload.hidden !== undefined) {
          vacancy.hidden = payload.hidden
            ? (vacancy.hidden?.length ? vacancy.hidden : [{ MarketVacancy_id: payload.vacancyId } as any])
            : [];
        }
        if (payload.interested !== undefined) {
          vacancy.interests = payload.interested
            ? (vacancy.interests?.length ? vacancy.interests : [{ MarketVacancy_id: payload.vacancyId, interest_level: InterestLevel.INTERESTED } as any])
            : [];
          // Team interests tab: keep the org-scoped cluster in sync with my own toggle.
          if (status === 4) {
            this.applyOwnTeamInterest(vacancy, payload.interested);
          }
        }
      });

    // Drop the row when it no longer belongs on the current tab. For the Team
    // tab, that is only when removing my own interest left the org-scoped
    // cluster empty (a colleague still interested keeps the row).
    const node = this.data.find(n => n.data.id === payload.vacancyId);
    const teamRowEmptied = payload.interested === false && status === 4
      && (node?.data?.team_interests?.length ?? 0) === 0;

    if (
      (payload.hidden === true && (status === 1 || status === 2 || status === 4))
      || (payload.hidden === false && status === 3)
      || (payload.interested === false && status === 2)
      || teamRowEmptied
    ) {
      this.data = this.data.filter(n => n.data.id !== payload.vacancyId);
      // A row leaving the current page can shrink the result set below what the
      // pager's cached total/last_page still claim (e.g. unhiding the last row on
      // the last page of the Hidden tab). Refetch the current page so pageResult
      // reflects the new total; DualAxis clamps to the last surviving page if the
      // removal emptied what is now the last page.
      this.dualAxis?.refresh();
    }

    this.mutationCountsRefresh$.next();
  }

  /**
   * Reflect the current user's own heart toggle in the org-scoped
   * `team_interests` cluster (Team tab), so the avatar appears/disappears
   * without a refetch. The me-scoped `interests` relation stays the toggle's
   * source of truth; this only mirrors my row into the shared cluster.
   */
  private applyOwnTeamInterest(vacancy: MarketVacancy, interested: boolean): void {
    const me = this.auth.user.getValue();
    if (!me?.id) return;
    const list = (vacancy.team_interests ??= []);
    const idx = list.findIndex(i => i.User_id === me.id);
    if (interested && idx === -1) {
      list.push({
        id: OPTIMISTIC_INTEREST_ID,
        User_id: me.id,
        MarketVacancy_id: vacancy.id,
        interest_level: InterestLevel.INTERESTED,
        user: {
          id: me.id,
          name_first: me.name_first,
          name_last: me.name_last,
          username: me.username,
          provider_avatar: me.provider_avatar,
          logo: me.logo,
        },
      });
    } else if (!interested && idx !== -1) {
      list.splice(idx, 1);
    }
  }

  public async convertToProject(vacancy: MarketVacancy) {
    if (confirm(`Convert "${vacancy.title}" to an internal project?`)) {
      try {
        const result = await this.marketVacancyService.convertToProject(vacancy.id);
        if (result && result.project) {
          this.router.navigate(['/projects', result.project.id]);
        }
      } catch (error) {
        console.error('Failed to convert vacancy', error);
      }
    }
  }

  public getInterestLevel(vacancy: MarketVacancy): string | null {
    if (vacancy.interests && vacancy.interests.length > 0) {
      return vacancy.interests[0].interest_level;
    }
    return null;
  }

  public getInterestBadgeStatus(level: string): string {
    return level === InterestLevel.INTERESTED ? 'info' : 'basic';
  }

  public goToUser(userId: number, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/users', userId]);
  }

  // Hover tooltip for the match-score ring: shows the best/selected match user and score.
  public scoreTooltip(vacancy: MarketVacancy): string {
    const ms: any = vacancy?.match_score;
    // Plan 3: a demoted pair (or a fallback aggregate) carries no score; the tooltip names the reason instead of the match.
    if (vacancy?.match_demoted) {
      return this.translate.instant(ms?.best_is_fallback ? 'eligibility.fallback' : 'eligibility.demoted');
    }
    const user = ms?.selected_user || ms?.best_user;
    if (!user) return '';
    const name = `${user.name_first ?? ''} ${user.name_last ?? ''}`.trim();
    if (!name) return '';
    const score = ms?.score !== null && ms?.score !== undefined ? ` (${this.translate.instant('match score')} ${Math.round(ms.score)}/100)` : '';
    return `${this.translate.instant('bestmatch')}: ${name}${score}`;
  }

  public userLogo(user: any): string | null {
    return user?.logo?.preview ? Globals.storage(user.logo.preview) : user?.provider_avatar || null;
  }

  public get isTeamTab(): boolean {
    return this.options?.others?.status === 4;
  }

  public teamInterestsOf(vacancy: MarketVacancy): MarketVacancyInterest[] {
    return vacancy.team_interests ?? [];
  }

  public colleagueName(interest: MarketVacancyInterest): string {
    const u = interest.user;
    if (!u) return '';
    return [u.name_first, u.name_last].filter(Boolean).join(' ') || u.username || '';
  }

  /** Names of the colleagues hidden behind the "+N" overflow chip (those past
   *  the first `shown`), comma-joined for the chip's hover tooltip. */
  public overflowColleagueNames(vacancy: MarketVacancy, shown = 5): string {
    return this.teamInterestsOf(vacancy).slice(shown).map(i => this.colleagueName(i)).filter(Boolean).join(', ');
  }

  public isExpired(vacancy: MarketVacancy): boolean {
    if (!vacancy.deadline) return false;
    return new Date(vacancy.deadline) < new Date();
  }

  /** Scraper dates are occasionally garbage (year 2900, year 2000 placeholders).
   *  Render those as '-' instead of an obviously wrong date. */
  public saneDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const y = new Date(value).getFullYear();
    return (y > 2000 && y < 2100) ? value : null;
  }

  public rowClass = (vacancy: MarketVacancy): string | null => {
    return vacancy.is_new ? 'is-new' : null;
  };

  private expandedSkills = new Set<number>();
  public toggleSkills(vacancyId: number, event: Event): void {
    event.stopPropagation();
    this.expandedSkills.has(vacancyId) ? this.expandedSkills.delete(vacancyId) : this.expandedSkills.add(vacancyId);
  }
  public skillsExpanded(vacancyId: number): boolean { return this.expandedSkills.has(vacancyId); }

  public get canManage(): boolean {
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  public get headerTitle(): string {
    return 'Market overview';
  }

  // Search methods
  public onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  private applySearch(query: string): void {
    if (!this.options?.filter) return;
    if (query && query.trim()) {
      this.options.filter['stuff'] = query.trim();
    } else {
      delete this.options.filter['stuff'];
    }
    this.events.next(DualAxisEvent.RESET);
  }

  public clearSearch(): void {
    this.searchQuery = '';
    this.applySearch('');
  }

  /** True when the "New" (unseen-by-me) filter is on. Drives the count-pill's pressed
   *  state; the New column funnel reads the same options.filter, so both stay in sync. */
  public get isNewFilterActive(): boolean {
    return this.options?.filter?.['is_new'] === true;
  }

  /** Toggle the "New" filter from the unseen-count pill: on = only unseen-by-me, off =
   *  cleared. Persists to the shared column-filter key (minus stuff/status, matching
   *  persistUserFilter / DualAxis.clearFilter) and refreshes via the same RESET path as
   *  search, so the funnel + Table-options panel reflect it. */
  public toggleNewFilter(): void {
    if (!this.options?.filter) return;
    if (this.isNewFilterActive) {
      delete this.options.filter['is_new'];
    } else {
      this.options.filter['is_new'] = true;
    }
    const filtersToSave = { ...this.options.filter };
    delete filtersToSave['stuff'];
    delete filtersToSave['status'];
    this.settings.putUserSetting('table_market_vacancies_filters', filtersToSave);
    this.events.next(DualAxisEvent.RESET);
  }

  public openFilterSheet(): void {
    if (!this.dualAxis) {
      console.error('openFilterSheet: dual-axis not ready');
      return;
    }
    this.dialogService.open(MarketFilterSheetComponent, {
      context: { list: this, dualAxis: this.dualAxis },
    });
  }

  // Filter count badge methods
  public hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  // Single source of truth for the active-filter count: the shared
  // activeFilterKeys() util (excludes the synthetic status/user_id/dates/stuff
  // keys — note this now also excludes `dates`, which the old inline filter did
  // not, unifying the funnel badge with the rest of the app). Feeds both the
  // funnel badge and the mobile-filters button.
  public getActiveFilterCount(): number {
    return activeFilterKeys(this.options?.filter).length;
  }

  public async markAllAsRead(): Promise<void> {
    const previousState = this.data.map(node => ({ node, was: node.data.is_new }));
    const previousCount = this.unseenCount;
    this.data.forEach(node => { node.data.is_new = false; });
    this.unseenCount = 0;
    try {
      await this.marketVacancyService.markAllSeen();
      // Nothing is unseen anymore, so a still-active "New" filter would otherwise
      // re-query for is_new=true and strand the user on an empty list (#1135).
      if (this.isNewFilterActive) {
        this.toggleNewFilter();
      }
    } catch (error) {
      previousState.forEach(({ node, was }) => { node.data.is_new = was; });
      this.unseenCount = previousCount;
      console.error('Failed to mark all as read', error);
    }
  }

  public get isActiveTab(): boolean {
    return this.options?.others?.status === 1;
  }

  /** The candidate match-score scope applies on the catalog tabs (Active,
   *  Inactive), which list the full vacancy set. It does not apply on the
   *  curated tabs — My Interests (= 2), Hidden (= 3), and Team interests (= 4) —
   *  where a per-candidate filter is not meaningful. */
  private get candidateScopeApplies(): boolean {
    const status = this.options?.others?.status;
    return status !== 2 && status !== 3 && status !== 4;
  }

  /** The candidate the list is scoped to right now, or null. Applies on the
   *  catalog tabs only; off them the selection is kept (shown greyed) but not
   *  applied. options.filter['user_id'] stays populated (ngOnInit seed +
   *  DualAxis.reset merge), so every candidate consumer reads it through here
   *  rather than re-implementing the tab check. */
  private get appliedCandidateId(): number | null {
    return this.candidateScopeApplies ? (this.options?.filter?.['user_id'] ?? null) : null;
  }

  private async loadUnseenCount(): Promise<void> {
    try {
      const result = await this.marketVacancyService.getUnseenCount();
      if (result && typeof result.count === 'number') {
        this.unseenCount = result.count;
      }
    } catch (error) {
      console.error('Failed to load unseen count', error);
    }
  }

  // getTabCounts() rejects on HTTP/network errors (#515); isValidTabCounts()
  // still guards a 2xx with a malformed body.
  private async loadTabCounts(): Promise<void> {
    const requestId = ++this.tabCountsRequestId;
    let result: MarketVacancyTabCounts | null = null;
    try {
      const response = await this.marketVacancyService.getTabCounts();
      result = this.isValidTabCounts(response) ? response : null;
      if (!result) {
        console.error('Failed to load tab counts: malformed or empty response', response);
      }
    } catch (error) {
      console.error('Failed to load tab counts', error);
    }
    if (requestId !== this.tabCountsRequestId) {
      return; // a newer request has already superseded this one
    }
    this.tabCounts = result;
    this.refreshTabLabels();
  }

  private isValidTabCounts(value: unknown): value is MarketVacancyTabCounts {
    const v = value as Partial<MarketVacancyTabCounts> | null | undefined;
    return !!v
      && typeof v === 'object'
      && typeof v.active === 'number'
      && typeof v.interests === 'number'
      && typeof v.hidden === 'number'
      && typeof v.inactive === 'number'
      && (v.team === undefined || typeof v.team === 'number');
  }

  /** Derives each tab's displayed title from its stable label plus the current
   *  count for that tab's route, if any is loaded yet. Called both when tabCounts
   *  changes and when a tab's translated label changes, so neither path can
   *  clobber the other (see the translate.get() subscription above). */
  private refreshTabLabels(): void {
    const countByRoute: { [route: string]: number | undefined } = {
      '/market/overview/active': this.tabCounts?.active,
      '/market/overview/interests': this.tabCounts?.interests,
      '/market/overview/team': this.tabCounts?.team,
      '/market/overview/hidden': this.tabCounts?.hidden,
      '/market/overview/inactive': this.tabCounts?.inactive,
    };
    this.tabs.forEach(tab => {
      const count = countByRoute[tab.route];
      tab.title = count !== undefined ? `${tab.label} (${count})` : tab.label;
    });
  }
}
